import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { Tooltip } from '@mui/material';
import styled, { css } from 'styled-components';

import {
  deleteOptionById,
  editOption,
  getFiltersByRoadmapId,
  getOptionsByFilterId,
  reorderFilterOptions,
} from '@/features/canvas/api';
import { FILTER_TITLE_MAX_LENGTH, TITLE_DISPLAY_LIMIT } from '@/features/canvas/constants';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { needsTruncation, truncateText } from '@/features/canvas/utils/text-utils';
import { useShowToast } from '@/hooks/useShowToast';
import { components } from '@/types/api';

const FilterOption = ({
  option,
  filterId,
  roadmapId,
  Icon,
  iconCursor = false,
}: {
  Icon: JSX.Element;
  filterId: number;
  option: components['schemas']['FilterOption'];
  roadmapId: number;
  iconCursor?: boolean;
}) => {
  const [inputValue, setInputValue] = useState<string>(option.title);
  const [editMode, setEditMode] = useState<boolean>(false);

  const { t } = useTranslation();
  const { showToast } = useShowToast();
  const queryClient = useQueryClient();

  const {
    setIsSidebarContentLoading,
    updateLastSavedTime,
    reorderedFilterOptions,
    setReorderedFilterOptions,
  } = useEditorContext();

  const inputRef = useRef<HTMLInputElement>(null);

  // Display truncated title when not editing
  const displayTitle = editMode ? inputValue : truncateText(inputValue, TITLE_DISPLAY_LIMIT);
  const showTooltip = !editMode && needsTruncation(inputValue, TITLE_DISPLAY_LIMIT);

  const { mutateAsync: deleteOption } = useMutation(async () => {
    try {
      await deleteOptionById(option.id!);
      updateLastSavedTime();
      showToast('success', t('editor.sidebar.filter.delete_option_success'));
    } catch (e) {
      showToast('error', t('editor.sidebar.filter.delete_option_error'));
    }
  });

  const { mutateAsync: updateOption } = useMutation(
    async (optionData: components['schemas']['FilterOption']) => {
      try {
        await editOption(optionData.id!, optionData);
        updateLastSavedTime();
        showToast('success', t('editor.sidebar.filter.edit_option_success'));
      } catch (e) {
        showToast('error', t('editor.sidebar.filter.edit_option_error'));
      }
    }
  );

  const handleFocus = () => {
    setEditMode(true);
  };

  const handleBlur = async () => {
    inputRef.current?.blur();
    setEditMode(false);

    if (!inputValue.trim()) {
      setInputValue(option.title);
      showToast('error', t('editor.sidebar.filter.option_title_required'));
      return;
    }

    if (inputValue === option.title) {
      return;
    }

    const updatedOption = { ...option, title: inputValue };
    await updateOption(updatedOption);

    setReorderedFilterOptions(prev => {
      const current = prev[filterId] || [];
      const updated = current.map(opt => (opt.id === updatedOption.id ? updatedOption : opt));
      return { ...prev, [filterId]: updated };
    });

    await queryClient.invalidateQueries([getOptionsByFilterId.name, filterId]);
    await queryClient.invalidateQueries([getFiltersByRoadmapId.name, roadmapId]);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    if (newValue.length <= FILTER_TITLE_MAX_LENGTH) {
      setInputValue(newValue);
    }
  };

  const handleKeyDown = async (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      await handleBlur();
    } else if (event.key === 'Escape') {
      setInputValue(option.title);
      setEditMode(false);
    }
  };

  const handleDelete = async () => {
    setIsSidebarContentLoading(true);

    await deleteOption();

    setReorderedFilterOptions(prev => {
      const current = prev[filterId] || [];
      const updated = current
        .filter(opt => opt.id !== option.id)
        .map((opt, index) => ({
          ...opt,
          sort_order: index,
        }));

      return {
        ...prev,
        [filterId]: updated,
      };
    });

    const reordered =
      reorderedFilterOptions[filterId]
        ?.filter((opt: components['schemas']['FilterOption']) => opt.id !== option.id)
        .map((opt: components['schemas']['FilterOption'], index: number) => ({
          id: opt.id!,
          sort_order: index,
        })) || [];

    await reorderFilterOptions(filterId, reordered);

    await queryClient.invalidateQueries([getOptionsByFilterId.name, filterId]);
    await queryClient.invalidateQueries([getFiltersByRoadmapId.name, roadmapId]);

    setIsSidebarContentLoading(false);
  };

  const OptionContent = (
    <OptionWrapper $editMode={editMode}>
      <StartOptionWrapper $pointerCursor={iconCursor}>
        {Icon}
        {editMode ? (
          <EditingWrapper>
            <TitleInput
              $editMode={editMode}
              type="text"
              ref={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              maxLength={FILTER_TITLE_MAX_LENGTH}
              autoFocus
            />
          </EditingWrapper>
        ) : (
          <TitleDisplay onClick={handleFocus}>{displayTitle}</TitleDisplay>
        )}
      </StartOptionWrapper>
      <DeleteWrapper onClick={handleDelete}>
        <StyledDelete />
      </DeleteWrapper>
    </OptionWrapper>
  );

  return showTooltip ? (
    <Tooltip title={inputValue} placement="top" arrow>
      {OptionContent}
    </Tooltip>
  ) : (
    OptionContent
  );
};

const OptionWrapper = styled.div<{ $editMode: boolean }>`
  height: auto;
  min-height: 25px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.palette.brand.backgroundDialog};
  border-radius: 1rem;
  padding: 0 0.5rem;

  ${({ $editMode, theme }) =>
    $editMode &&
    css`
      background-color: ${theme.palette.brand.white};
      border: 1px solid ${theme.palette.brand.backgroundDialog};
      padding: 0.25rem 0.5rem;
    `}
`;

const StartOptionWrapper = styled.div<{ $pointerCursor: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  ${({ $pointerCursor }) =>
    $pointerCursor &&
    css`
      cursor: pointer;
    `}
`;

const EditingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TitleInput = styled.input<{ $editMode: boolean }>`
  width: 100%;
  border: none;
  background: transparent;
  outline: none;
  font-size: 13px;

  ${({ $editMode }) =>
    $editMode &&
    css`
      padding: 4px 0;
    `}
`;

const TitleDisplay = styled.div`
  cursor: pointer;
  font-size: 13px;
  width: 100%;
  padding: 4px 0;
`;

const DeleteWrapper = styled.div`
  height: 100%;
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
`;

const StyledDelete = styled.div`
  width: 8px;
  height: 2px;
  background-color: ${({ theme }) => theme.palette.brand.textSecondary};
`;

export { FilterOption };
